/**
 * companion.js — Camlin Companion embed entry (doc 116 §2).
 * Async script tag; reads data-camlin-companion-key from its own tag; no layout shift.
 *
 * Host usage (cache-busted per ios-cache-busting.mdc):
 * <script
 *   src="https://companion.camlinconnect.com.au/companion.js?v=<hash>"
 *   data-camlin-companion-key="cmp_<tenantId>_<base32>"
 *   async
 * ></script>
 */

import { bootCompanion, findCompanionScriptTag } from './boot';

declare global {
  interface Window {
    CamlinCompanion?: {
      boot: typeof bootCompanion;
      version: string;
      ask: typeof askCompanion;
    };
  }
}

/**
 * "Ask the avatar about this" convenience (bundle 2.9.0, nua-dot-com
 * DEMO_STRATEGY §4.3): dispatches the same `camlin-companion:ask` window
 * event the widget listens for. `opts.source` (a host section id) rides in
 * the event detail so host pages can correlate their own analytics; host
 * pages may also feature-detect this function to light up shortcut buttons.
 */
function askCompanion(prompt: string, opts?: { source?: string }): void {
  window.dispatchEvent(
    new CustomEvent('camlin-companion:ask', { detail: { prompt, source: opts?.source } })
  );
}

// Injected at build time from package.json (vite.config.companion.ts
// `define`) so one number names both the SDK and the hosted embed —
// companion-sdk-packaging-plan §3. The fallback only appears in dev
// serves that skip the define.
declare const __COMPANION_EMBED_VERSION__: string | undefined;
const COMPANION_EMBED_VERSION =
  typeof __COMPANION_EMBED_VERSION__ !== 'undefined' ? __COMPANION_EMBED_VERSION__ : '0.0.0-dev';

function scheduleBoot(): void {
  const script = findCompanionScriptTag();
  void bootCompanion({ scriptElement: script }).then((result) => {
    if (!result.ok) {
      console.warn('[Camlin Companion] boot refused:', result.refusalReason, result.message);
    }
  });
}

if (typeof window !== 'undefined') {
  window.CamlinCompanion = {
    boot: bootCompanion,
    version: COMPANION_EMBED_VERSION,
    ask: askCompanion
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleBoot, { once: true });
  } else {
    scheduleBoot();
  }
}

export { bootCompanion, findCompanionScriptTag };
export * from './types';
export * from './siteKey';
export * from './attributes';
